<template>
    <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" modal :header="dialogHeader" style="width: 25rem">
        <form @submit.prevent="saveGroup">
            <div class="user-group-edit-dialog__field">
                <label for="usergroup-name" class="user-group-edit-dialog__label">Navn</label>
                <InputText id="usergroup-name" v-model="groupName" style="width: 100%" :invalid="!!errors.name" autofocus />
                <small v-if="errors.name" class="user-group-edit-dialog__error">{{ errors.name[0] }}</small>
            </div>
            <div class="user-group-edit-dialog__dialog-footer">
                <Button type="button" label="Avbryt" severity="secondary" @click="$emit('update:visible', false)" />
                <Button type="submit" label="Lagre" :loading="saving" />
            </div>
        </form>

        <div v-if="group" class="user-group-edit-dialog__field">
            <label class="user-group-edit-dialog__label">Brukere</label>

            <div class="user-group-edit-dialog__users">
                <Chip
                    v-for="user of groupUsers"
                    :key="user.id"
                    :label="`${user.name} (${user.email})`"
                    removable
                    @remove="removeUser(user)"
                />
                <span v-if="groupUsers.length === 0" class="user-group-edit-dialog__users-empty">
                    Ingen brukere i denne gruppen.
                </span>
            </div>

            <AutoComplete
                v-model="userQuery"
                :suggestions="userSuggestions"
                optionLabel="name"
                placeholder="Søk etter navn eller e-post"
                style="width: 100%"
                :inputStyle="{ width: '100%' }"
                @complete="searchUsers"
                @item-select="addUser($event.value)"
            >
                <template #option="{ option }">
                    {{ option.name }} <span class="user-group-edit-dialog__option-email">({{ option.email }})</span>
                </template>
            </AutoComplete>
        </div>

        <Card v-if="group" class="user-group-edit-dialog__danger-zone">
            <template #title>Faresone</template>
            <template #content>
                <p class="user-group-edit-dialog__danger-text">
                    Advarsel: Slettingen er permanent. Gruppen vil bli borte for alltid.
                </p>
                <Button label="Slett gruppen" icon="pi pi-trash" severity="danger" @click="confirmDelete" />
            </template>
        </Card>
    </Dialog>
</template>
<script>
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Card from 'primevue/card';
import Chip from 'primevue/chip';
import AutoComplete from 'primevue/autocomplete';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content;
}

export default {
    components: {
        Dialog,
        InputText,
        Button,
        Card,
        Chip,
        AutoComplete,
    },
    props: {
        visible: { type: Boolean, required: true },
        group: { type: Object, default: null },
        allUsers: { type: Array, default: () => [] },
    },
    emits: ['update:visible', 'saved', 'deleted', 'member-count-changed'],
    setup() {
        return {
            toast: useToast(),
            confirm: useConfirm(),
        };
    },
    data() {
        return {
            groupName: '',
            groupUsers: [],
            userQuery: '',
            userSuggestions: [],
            errors: {},
            saving: false,
        };
    },
    computed: {
        dialogHeader() {
            return this.group ? 'Rediger brukergruppe' : 'Legg til brukergruppe';
        },
    },
    watch: {
        visible(isVisible) {
            if (!isVisible) {
                return;
            }

            this.groupName = this.group ? this.group.name : '';
            this.groupUsers = [...(this.group?.users ?? [])];
            this.errors = {};
            this.userQuery = '';
            this.userSuggestions = [];
        },
    },
    methods: {
        searchUsers(event) {
            const term = event.query.trim().toLowerCase();
            const memberIds = new Set(this.groupUsers.map(u => u.id));

            this.userSuggestions = this.allUsers
                .filter(user => !memberIds.has(user.id))
                .filter(user =>
                    user.name.toLowerCase().includes(term) ||
                    user.email.toLowerCase().includes(term)
                );
        },
        async addUser(user) {
            this.userQuery = '';

            try {
                const response = await fetch(`/api/v1/user/${user.id}/groups`, {
                    method: 'POST',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken(),
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ usergroup_id: this.group.id }),
                });

                if (!response.ok) {
                    throw new Error('request failed');
                }

                this.groupUsers.push({ id: user.id, name: user.name, email: user.email });
                this.$emit('member-count-changed', { groupId: this.group.id, delta: 1 });
            } catch (error) {
                console.error(error);
                this.toast.add({ severity: 'error', summary: 'Feil', detail: 'Kunne ikke legge til brukeren.', life: 3000 });
            }
        },
        async removeUser(user) {
            try {
                const response = await fetch(`/api/v1/user/${user.id}/groups/${this.group.id}`, {
                    method: 'DELETE',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken(),
                        Accept: 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('request failed');
                }

                this.groupUsers = this.groupUsers.filter(u => u.id !== user.id);
                this.$emit('member-count-changed', { groupId: this.group.id, delta: -1 });
            } catch (error) {
                console.error(error);
                this.toast.add({ severity: 'error', summary: 'Feil', detail: 'Kunne ikke fjerne brukeren.', life: 3000 });
            }
        },
        confirmDelete() {
            this.confirm.require({
                message: `Er du sikker på at du vil slette "${this.group.name}"?`,
                header: 'Bekreft sletting',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Slett',
                rejectLabel: 'Avbryt',
                acceptProps: { severity: 'danger' },
                accept: () => this.deleteGroup(),
            });
        },
        async deleteGroup() {
            try {
                const response = await fetch(`/api/v1/usergroup/${this.group.id}`, {
                    method: 'DELETE',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken(),
                        Accept: 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('request failed');
                }

                this.$emit('deleted', this.group.id);
                this.$emit('update:visible', false);
                this.toast.add({ severity: 'success', summary: 'Slettet', detail: `"${this.group.name}" ble slettet.`, life: 3000 });
            } catch (error) {
                console.error(error);
                this.toast.add({ severity: 'error', summary: 'Feil', detail: 'Kunne ikke slette gruppen.', life: 3000 });
            }
        },
        async saveGroup() {
            this.errors = {};
            this.saving = true;

            const isEditing = !!this.group;
            const url = isEditing ? `/api/v1/usergroup/${this.group.id}` : '/admin/usergroup';

            try {
                const response = await fetch(url, {
                    method: isEditing ? 'PUT' : 'POST',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken(),
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ name: this.groupName }),
                });

                if (response.status === 422) {
                    const data = await response.json();
                    this.errors = data.errors ?? {};
                    return;
                }

                if (!response.ok) {
                    throw new Error('request failed');
                }

                const group = await response.json();
                this.$emit('saved', group);
                this.$emit('update:visible', false);
                this.toast.add({
                    severity: 'success',
                    summary: 'Lagret',
                    detail: isEditing ? 'Brukergruppen ble oppdatert.' : 'Brukergruppen ble opprettet.',
                    life: 3000,
                });
            } catch (error) {
                console.error(error);
                this.toast.add({ severity: 'error', summary: 'Feil', detail: 'Kunne ikke lagre gruppen.', life: 3000 });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
<style scoped>
.user-group-edit-dialog__field {
    margin-bottom: 1.25rem;
}

.user-group-edit-dialog__label {
    display: block;
    margin-bottom: 0.5rem;
}

.user-group-edit-dialog__error {
    color: var(--p-red-500, #e24c4c);
}

.user-group-edit-dialog__dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.user-group-edit-dialog__danger-zone {
    margin-top: 1.5rem;
    border: 1px solid var(--p-red-500, #e24c4c);
}

.user-group-edit-dialog__danger-zone :deep(.p-card-title) {
    color: var(--p-red-500, #e24c4c);
}

.user-group-edit-dialog__danger-text {
    margin-top: 0;
}

.user-group-edit-dialog__users {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}

.user-group-edit-dialog__users-empty {
    color: var(--p-text-muted-color, #6c757d);
    font-size: 0.9rem;
}

.user-group-edit-dialog__option-email {
    color: var(--p-text-muted-color, #6c757d);
    font-size: 0.9rem;
}
</style>
